import { getBrandParent } from 'country-configuration/utils/brands/getBrandParent'
import { getSubbrands } from 'country-configuration/utils/brands/getSubBrands'
import { mapLabelKeysToBrands } from 'country-configuration/utils/brands/mapLabelKeysToBrands'
import type { ReadonlyHeaders } from 'next/dist/server/web/spec-extension/adapters/headers'
import type { PageType } from 'types/analytics'
import type { Brand } from 'types/brands'
import { Channel, type MasterData, Platform } from 'types/masterData'
import type { CustomDomainData } from 'types/masterData/domainData/domainData'
import { env } from 'utils/envManager/envManager'
import { getABTestFromHeaders } from 'utils/getABTestFromHeaders/getABTestFromHeaders'
import { getCustomDateFromHeaders } from 'utils/getCustomDateFromHeaders/getCustomDateFromHeaders'
import { getDeviceFromHeaders } from 'utils/getDeviceFromHeaders/getDeviceFromHeaders'
import { getRegionFromHeaders } from 'utils/getRegionFromHeaders/getRegionFromHeaders'
import { getTestFromHeaders } from 'utils/getTestFromHeaders/getTestFromHeaders'

const initialState: Partial<MasterData> = {}

let masterData: Partial<MasterData> = initialState
let custom: CustomDomainData | null = null
let isMainHeadingLogo = false
let pageType: PageType | null = null
let brand: Brand['id'] | null = null
let subBrand: Brand['id'] | null = null

export const resetMasterData = () => {
	masterData = {}
	custom = null
	isMainHeadingLogo = false
	pageType = null
	brand = null
	subBrand = null
}

export const setMasterData = ({
	headersList,
}: {
	headersList: ReadonlyHeaders
}): void => {
	const url = headersList.get('x-url') as string
	// livePreviewHash: live preview hash for content stack banner editting
	const isCicd = Boolean(
		headersList.get('x-is-lighthouse') ?? url?.includes('livePreviewHash')
	)
	const isQa = !!headersList.get('x-qa')
	// ios and android only on checkout
	const platform = (headersList.get('x-platform') as Platform) ?? Platform.Web
	const dev = headersList.get('x-dev')
	const referer = headersList.get('referer')
	const userAgent = headersList.get('user-agent')

	const { isDesktop, isMobile, device } = getDeviceFromHeaders(headersList)
	const { test } = getTestFromHeaders(headersList)
	const { customDate } = getCustomDateFromHeaders(headersList)
	const { region } = getRegionFromHeaders(headersList)

	const countryConfHeader = headersList.get('x-country-configuration') as string

	const { abTestPage, abTestLayout } = getABTestFromHeaders(headersList)

	// Need this validation because error/redirect pages refresh all except middleware
	if (countryConfHeader) {
		const countryConfiguration = JSON.parse(
			decodeURIComponent(countryConfHeader)
		)

		const {
			additionalCurrencies,
			brands,
			countryISO,
			countryCode,
			countryName,
			currency,
			hasDiscount,
			hasOnlineProducts,
			hasOnlineSale,
			hasPriceHistory,
			isDefaultLanguage,
			isDiscountSeason,
			isLoyalty,
			isMultiLanguage,
			isRtl,
			kcSafetyGenderIds,
			languageISO,
			languageName,
			legal,
			locale,
			loyalty,
			loyaltyProgram,
			mangoISO,
			measurementUnits,
			mngLogo,
			refund,
			shippingInfo,
			shoppingCart,
			shouldShowDiscountRate,
			shouldShowImportData,
			shouldShowReleasesMicrofibers,
			warehouse,
		} = countryConfiguration

		masterData = {
			...masterData,
			abTestPage,
			abTestLayout,
			platform,
			channel: env.NEXT_PUBLIC_CHANNEL,
			isShop: env.NEXT_PUBLIC_CHANNEL === Channel.Shop,
			isApp: env.NEXT_PUBLIC_IS_APP === 'true',
			environment: env.NEXT_PUBLIC_ENV,
			brand: null,
			subBrand: null,
			device,
			isMobile,
			isDesktop,
			customDate,
			userAgent,
			isCicd,
			isQa,
			dev,
			url,
			referer,
			region,
			test,
			country: {
				// TODO: temporary hack to map proper keys till country config service returns the appropiate ones.
				brands: mapLabelKeysToBrands(brands),
				additionalCurrencies,
				countryISO,
				countryCode,
				countryName,
				currency,
				hasDiscount,
				hasOnlineProducts,
				hasOnlineSale,
				hasPriceHistory,
				isDefaultLanguage,
				isDiscountSeason,
				isLoyalty,
				isMultiLanguage,
				isRtl,
				kcSafetyGenderIds,
				languageISO,
				languageName,
				legal,
				locale,
				loyalty,
				loyaltyProgram,
				mangoISO,
				measurementUnits,
				mngLogo,
				refund,
				shippingInfo,
				shoppingCart,
				shouldShowDiscountRate,
				shouldShowImportData,
				shouldShowReleasesMicrofibers,
				warehouse,
			},
		}
	}
}

/** @throws {Error} If master data is not initialized */
export const getMasterData = (): MasterData => {
	if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test') {
		throw new Error(
			'[MasterData] getMasterData should be called on the server side only.'
		)
	}
	if (!masterData.country) {
		throw new Error(
			'[MasterData] Country info is not set. Did you forget calling setMasterData before? You should call setMasterData with parameters at least once.'
		)
	}
	return {
		...masterData,
		custom,
		isMainHeadingLogo,
		pageType,
		brand,
		subBrand,
	} as MasterData
}

export const setPageType = (pageTypeData: PageType): void => {
	pageType = pageTypeData
}

export const setIsMainHeadingLogo = (isMainHeadingLogoValue: boolean): void => {
	isMainHeadingLogo = isMainHeadingLogoValue
}

export const setBrand = (brandId: Brand['id'] | null): void => {
	if (!brandId) {
		return
	}

	const countryBrands = masterData?.country?.brands || []
	const parentBrand = getBrandParent(brandId, countryBrands)
	const countrySubBrandIds = getSubbrands(countryBrands).map(
		(subBrandData) => subBrandData.id
	)
	const isSubBrand = countrySubBrandIds.includes(brandId)

	brand = parentBrand.id

	if (isSubBrand) {
		subBrand = brandId
	}
}

export const setSubBrand = (subBrandId?: Brand['id'] | null): void => {
	if (!subBrandId) {
		return
	}

	subBrand = subBrandId
}

export const setCustom = (customData: CustomDomainData): void => {
	custom = { ...custom, ...customData }
}
